import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, Chip, TextField, Typography } from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AppDialog from "../../components/AppBase/AppDialog";
import AppConfirmDialog from "../../components/AppBase/AppConfirmDialog";
import { theme } from "../../themes/theme";
import { AppState } from "../../redux/store";
import { RoleState } from "../../types/models/RoleState";
import { fetchError, updateUserOptInRoles, updateUserLocations } from "../../redux/actions";
import { OrgUser } from "./OrgUser";
import {
  getLocationDisplayName,
  getRoleBundleDisplayName,
  getRoleDisplayName,
} from "./orgUserDisplay";

interface UserDetailModalProps {
  open: boolean;
  onClose: () => void;
  user?: OrgUser;
  isAdmin: boolean;
  onSave: (user: OrgUser) => void;
}

const ChipList: React.FC<{ values: string[]; emptyLabel: string }> = ({ values, emptyLabel }) => {
  if (values.length === 0) {
    return (
      <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>{emptyLabel}</Typography>
    );
  }
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
      {values.map((value) => (
        <Chip key={value} label={value} size="small" />
      ))}
    </Box>
  );
};

interface MultiSelectChipInputProps {
  label: string;
  options: string[];
  values: string[];
  onChange: (values: string[]) => void;
  getOptionLabel: (id: string) => string;
}

const MultiSelectChipInput: React.FC<MultiSelectChipInputProps> = ({
  label,
  options,
  values,
  onChange,
  getOptionLabel,
}) => (
  <Autocomplete
    multiple
    size="small"
    disableCloseOnSelect
    value={values}
    options={options}
    getOptionLabel={getOptionLabel}
    onChange={(_event, newValues) => onChange(newValues)}
    renderInput={(params) => (
      <TextField
        {...params}
        placeholder={values.length === 0 ? `No ${label.toLowerCase()} assigned` : undefined}
      />
    )}
    renderTags={(tagValues: string[], getTagProps) =>
      tagValues.map((value, index) => (
        <Chip
          {...getTagProps({ index })}
          key={value}
          label={getOptionLabel(value)}
          size="small"
          deleteIcon={<CancelIcon onMouseDown={(event) => event.stopPropagation()} />}
        />
      ))
    }
  />
);

const DetailSection: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box sx={{ mb: 3 }}>
    <Typography sx={{ fontWeight: 600, mb: 1 }}>{label}</Typography>
    <span>{children}</span>
  </Box>
);

type SaveField = "roles" | "locations";

const fieldLabels: Record<SaveField, string> = {
  roles: "opt-in roles",
  locations: "locations",
};

const UserDetailModal: React.FC<UserDetailModalProps> = ({
  open,
  onClose,
  user,
  isAdmin,
  onSave,
}) => {
  const dispatch = useDispatch();
  const roleState = useSelector<AppState, RoleState>((state) => state.role);

  const [editedOptInRoles, setEditedOptInRoles] = useState<string[]>([]);
  const [editedLocations, setEditedLocations] = useState<string[]>([]);
  // Tracks what's actually been saved so far, independent of the `user` prop
  // (which is a stale snapshot that doesn't live-update from Redux mid-session).
  const [committedOptInRoles, setCommittedOptInRoles] = useState<string[]>([]);
  const [committedLocations, setCommittedLocations] = useState<string[]>([]);
  const [openDiscardConfirm, setOpenDiscardConfirm] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    if (user) {
      setEditedOptInRoles(user.OptInRoleIds);
      setEditedLocations(user.LniIds);
      setCommittedOptInRoles(user.OptInRoleIds);
      setCommittedLocations(user.LniIds);
    }
  }, [user]);

  if (!user) return null;

  const optInRoleOptions = roleState.knownRoles?.roles.map((role) => role.RoleId) ?? [];
  const locationOptions = roleState.knownRoles?.locations.map((location) => location.LNIId) ?? [];
  const getOptInRoleLabel = (roleId: string) => getRoleDisplayName(roleId, roleState.knownRoles?.roles);
  const getLocationLabel = (lniId: string) =>
    getLocationDisplayName(lniId, roleState.knownRoles?.locations);

  const arraysMatch = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
  };

  const hasUnsavedChanges =
    !arraysMatch(editedOptInRoles, committedOptInRoles) ||
    !arraysMatch(editedLocations, committedLocations);

  const discardChanges = () => {
    setEditedOptInRoles(committedOptInRoles);
    setEditedLocations(committedLocations);
    setOpenDiscardConfirm(false);
    onClose();
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setOpenDiscardConfirm(true);
      return;
    }
    onClose();
  };

  const handleSave = () => {
    const optInRoleIds = editedOptInRoles;
    const locationIds = editedLocations;

    const rolesChanged = !arraysMatch(editedOptInRoles, committedOptInRoles);
    const locationsChanged = !arraysMatch(editedLocations, committedLocations);

    if (!rolesChanged && !locationsChanged) {
      onClose();
      return;
    }

    setIsSaving(true);

    const tasks: Promise<{ field: SaveField; success: boolean }>[] = [];

    if (rolesChanged) {
      tasks.push(
        new Promise((resolve) => {
          dispatch(
            updateUserOptInRoles(
              user.Id,
              optInRoleIds,
              (success) => resolve({ field: "roles", success }),
              true
            )
          );
        })
      );
    }

    if (locationsChanged) {
      tasks.push(
        new Promise((resolve) => {
          dispatch(
            updateUserLocations(
              user.Id,
              locationIds,
              (success) => resolve({ field: "locations", success }),
              true
            )
          );
        })
      );
    }

    Promise.all(tasks).then((results) => {
      setIsSaving(false);

      const succeeded = results.filter((result) => result.success).map((result) => result.field);
      const failed = results.filter((result) => !result.success).map((result) => result.field);

      if (succeeded.includes("roles")) setCommittedOptInRoles(editedOptInRoles);
      if (succeeded.includes("locations")) setCommittedLocations(editedLocations);

      if (failed.length === 0) {
        onSave({ ...user, OptInRoleIds: optInRoleIds, LniIds: locationIds });
        onClose();
        return;
      }

      // Partial or full failure: keep the modal open so the admin can retry the fields that failed.
      // Reflect whatever did succeed in the table.
      if (succeeded.length > 0) {
        onSave({
          ...user,
          OptInRoleIds: succeeded.includes("roles") ? optInRoleIds : user.OptInRoleIds,
          LniIds: succeeded.includes("locations") ? locationIds : user.LniIds,
        });
      }

      const failedLabels = failed.map((field) => fieldLabels[field]).join(" and ");
      const succeededLabels = succeeded.map((field) => fieldLabels[field]).join(" and ");
      dispatch(
        fetchError(
          succeeded.length > 0
            ? `Failed to update ${failedLabels}. ${succeededLabels} ${
                succeeded.length === 1 ? "was" : "were"
              } saved successfully.`
            : `Failed to update ${failedLabels}. Please try again.`
        )
      );
    });
  };

  return (
    <>
      <AppDialog
        open={open}
        onClose={handleCancel}
        dividers
        title={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontWeight: 600 }}>{user.Name || user.Email}</Typography>
            <Typography sx={{ fontSize: 13, color: theme.table.subTextColor }}>
              {user.Email}
            </Typography>
          </Box>
        }
        footer={
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, width: "100%" }}>
            <Button
              variant="outlined"
              sx={{ textTransform: "none" }}
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ textTransform: "none", color: "white" }}
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </Box>
        }
      >
        <DetailSection label="Role Bundles">
          <ChipList
            values={user.RoleBundleIds.map((id) =>
              getRoleBundleDisplayName(id, roleState.roleBundles)
            )}
            emptyLabel="No role bundles assigned"
          />
        </DetailSection>
        <DetailSection label="Opt-in Roles">
          <MultiSelectChipInput
            label="Opt-in Roles"
            options={optInRoleOptions}
            values={editedOptInRoles}
            onChange={setEditedOptInRoles}
            getOptionLabel={getOptInRoleLabel}
          />
        </DetailSection>
        <DetailSection label="Locations">
          <MultiSelectChipInput
            label="Locations"
            options={locationOptions}
            values={editedLocations}
            onChange={setEditedLocations}
            getOptionLabel={getLocationLabel}
          />
        </DetailSection>
        {isAdmin && (
          <DetailSection label="Organisation">
            <Typography sx={{ fontSize: 14 }}>{user.Organisation ?? "—"}</Typography>
          </DetailSection>
        )}
      </AppDialog>
      <AppConfirmDialog
        open={openDiscardConfirm}
        onDeny={() => setOpenDiscardConfirm(false)}
        onConfirm={discardChanges}
        dialogTitle="Discard changes?"
        title="You have unsaved changes to this user's opt-in roles or locations. Discard them?"
      />
    </>
  );
};

export default UserDetailModal;
